/**
 * Admin → Document fields (tasks/14): which Document Detail tabs — and, for
 * Overview/Metadata, which fields within them — this partner's tenants see.
 * A parent/child tree: unchecking a tab collapses its own field list instead
 * of merely greying it out, so checking "Overview" is what reveals its
 * fields, rather than a flat list of unrelated checkboxes.
 *
 * A display preference, not a security control (see
 * `backend/app/domain/document_fields.py`) — hiding a field here only changes
 * what the Document Detail screen renders; the underlying data is unchanged.
 */
import { useCallback, useEffect, useState } from 'react';

import type {
  DocumentFieldVisibility,
  DocumentTab,
  MetadataField,
  OverviewField,
} from '../../api/schema';
import { Button, Checkbox, EmptyState, ErrorState, Skeleton, useToast } from '../../components';
import { useLoadState } from '../../lib/useLoadState';

import { useAdminApi } from './api';
import { useOwnPartner } from './useAdmin';
import './admin.css';

const TAB_IDS: readonly DocumentTab[] = ['overview', 'ocr_text', 'metadata', 'history'];

const OVERVIEW_FIELDS: readonly { id: OverviewField; label: string }[] = [
  { id: 'ai_summary', label: 'AI summary' },
  { id: 'classification_rationale', label: 'Classification rationale ("Why:")' },
  { id: 'classification_versions', label: 'Model / prompt / taxonomy version chips' },
];

const METADATA_FIELDS: readonly { id: MetadataField; label: string }[] = [
  { id: 'filename', label: 'Filename' },
  { id: 'status', label: 'Status' },
  { id: 'location', label: 'Location' },
  { id: 'scanner', label: 'Scanner' },
  { id: 'source', label: 'Source channel' },
  { id: 'operator_ref', label: 'Operator reference' },
  { id: 'scanned_at', label: 'Scanned at' },
  { id: 'ingested_at', label: 'Ingested at' },
  { id: 'pages', label: 'Pages' },
  { id: 'size', label: 'Size' },
  { id: 'language', label: 'Language' },
  { id: 'ocr_confidence', label: 'OCR confidence' },
  { id: 'content_hash', label: 'Content hash' },
  { id: 'custom_metadata', label: 'Custom metadata (every tenant key)' },
];

function toggled<T>(set: ReadonlySet<T>, id: T): Set<T> {
  const next = new Set(set);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

interface Draft {
  tabs: Set<DocumentTab>;
  overview: Set<OverviewField>;
  metadata: Set<MetadataField>;
}

function toDraft(visibility: DocumentFieldVisibility): Draft {
  return {
    tabs: new Set(visibility.hidden_tabs),
    overview: new Set(visibility.hidden_overview_fields),
    metadata: new Set(visibility.hidden_metadata_fields),
  };
}

function equalDraft(a: Draft, b: Draft): boolean {
  const same = <T,>(x: ReadonlySet<T>, y: ReadonlySet<T>): boolean =>
    x.size === y.size && [...x].every((v) => y.has(v));
  return same(a.tabs, b.tabs) && same(a.overview, b.overview) && same(a.metadata, b.metadata);
}

/** One tab and, while it's shown, its own nested field checkboxes. Selecting
 * the tab is what reveals the children — there is nothing to see under a
 * hidden tab, so there's nothing to grey out either. */
function TabNode<F extends string = never>({
  label,
  shown,
  onToggleTab,
  fields,
  hidden,
  onToggleField,
}: {
  label: string;
  shown: boolean;
  onToggleTab: () => void;
  fields?: readonly { id: F; label: string }[];
  hidden?: ReadonlySet<F>;
  onToggleField?: (id: F) => void;
}): React.JSX.Element {
  return (
    <div className="adm-docfields-node">
      <Checkbox className="adm-docfields-node__tab" checked={shown} onChange={onToggleTab}>
        Show {label}
      </Checkbox>
      {shown && fields !== undefined && hidden !== undefined && onToggleField !== undefined ? (
        <div className="adm-docfields-children" role="group" aria-label={`${label} fields`}>
          {fields.map((field) => (
            <Checkbox
              key={field.id}
              checked={!hidden.has(field.id)}
              onChange={() => { onToggleField(field.id); }}
            >
              Show {field.label}
            </Checkbox>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function DocumentFieldsEditor({ partnerId }: { partnerId: string }): React.JSX.Element {
  const api = useAdminApi();
  const { toast } = useToast();

  const fetchConfig = useCallback(
    (signal: AbortSignal): Promise<DocumentFieldVisibility> => api.documentFields.get(partnerId, signal),
    [api, partnerId],
  );
  const state = useLoadState(fetchConfig);

  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);

  const loaded = state.status === 'ready' ? state.data : null;
  useEffect(() => {
    if (loaded !== null) setDraft(toDraft(loaded));
  }, [loaded]);

  const hidingEveryTab = draft !== null && draft.tabs.size === TAB_IDS.length;
  const dirty = draft !== null && loaded !== null && !equalDraft(draft, toDraft(loaded));

  const save = useCallback(() => {
    if (draft === null) return;
    setSaving(true);
    void (async () => {
      try {
        await api.documentFields.update(partnerId, {
          hidden_tabs: [...draft.tabs],
          hidden_overview_fields: [...draft.overview],
          hidden_metadata_fields: [...draft.metadata],
        });
        toast('Document field visibility saved');
        if (state.status === 'ready') state.reload();
      } catch {
        toast('Could not save — try again', { tone: 'error' });
      } finally {
        setSaving(false);
      }
    })();
  }, [draft, partnerId, api, toast, state]);

  if (state.status === 'loading') {
    return (
      <section className="adm-card" aria-label="Document field visibility">
        <Skeleton height={360} />
      </section>
    );
  }

  if (state.status === 'error') {
    return (
      <section className="adm-card" aria-label="Document field visibility">
        <ErrorState
          title="Couldn't load document field visibility"
          body="The partner config service didn't respond."
          traceId={state.traceId ?? undefined}
          actions={
            <Button variant="secondary" onClick={state.reload}>
              Retry
            </Button>
          }
        />
      </section>
    );
  }

  if (draft === null) return <section className="adm-card" aria-label="Document field visibility" />;

  return (
    <section className="adm-card" aria-labelledby="adm-doc-fields-h">
      <header className="adm-card__head">
        <div>
          <h2 id="adm-doc-fields-h" className="adm-card__title">
            Document field visibility
          </h2>
          <p className="adm-card__sub">
            Select a tab to show its own fields — this partner's tenants see exactly this on every
            classified document
          </p>
        </div>
      </header>

      <div className="adm-docfields-tree">
        <TabNode
          label="Overview"
          shown={!draft.tabs.has('overview')}
          onToggleTab={() => { setDraft({ ...draft, tabs: toggled(draft.tabs, 'overview') }); }}
          fields={OVERVIEW_FIELDS}
          hidden={draft.overview}
          onToggleField={(id) => { setDraft({ ...draft, overview: toggled(draft.overview, id) }); }}
        />
        <TabNode
          label="OCR text"
          shown={!draft.tabs.has('ocr_text')}
          onToggleTab={() => { setDraft({ ...draft, tabs: toggled(draft.tabs, 'ocr_text') }); }}
        />
        <TabNode
          label="Metadata"
          shown={!draft.tabs.has('metadata')}
          onToggleTab={() => { setDraft({ ...draft, tabs: toggled(draft.tabs, 'metadata') }); }}
          fields={METADATA_FIELDS}
          hidden={draft.metadata}
          onToggleField={(id) => { setDraft({ ...draft, metadata: toggled(draft.metadata, id) }); }}
        />
        <TabNode
          label="History"
          shown={!draft.tabs.has('history')}
          onToggleTab={() => { setDraft({ ...draft, tabs: toggled(draft.tabs, 'history') }); }}
        />
      </div>

      {hidingEveryTab ? (
        <p className="adm-docfields-warn" role="alert">
          At least one tab must stay visible — the Document Detail screen cannot show nothing.
        </p>
      ) : null}

      <div className="adm-docfields-actions">
        <Button variant="primary" loading={saving} disabled={!dirty || hidingEveryTab} onClick={save}>
          Save
        </Button>
      </div>
    </section>
  );
}

export function DocumentFieldsScreen(): React.JSX.Element {
  const state = useOwnPartner();

  if (state.status === 'loading') {
    return (
      <div className="adm-screen" aria-busy="true">
        <Skeleton height={360} />
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="adm-screen">
        <ErrorState
          title="Couldn't load your partner"
          body="The partner service didn't respond."
          traceId={state.traceId ?? undefined}
          actions={
            <Button variant="secondary" onClick={state.reload}>
              Retry
            </Button>
          }
        />
      </div>
    );
  }

  if (state.data === null) {
    return (
      <div className="adm-screen">
        <EmptyState
          title="No partner yet"
          body="Document field visibility is configured per partner — create a partner first."
        />
      </div>
    );
  }

  return (
    <div className="adm-screen">
      <DocumentFieldsEditor partnerId={state.data.id} />
    </div>
  );
}
